import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import { createFieldTextArea } from "../../../components/input/template-builder/template-builder.logic";
import {
    createColumnDate,
    createColumnDatetime,
    createColumnInput,
    createColumnSelect,
    createColumnTreeSelect
} from "../../../components/table/c-table.logic";
import i18n from "../../../i18n";
import { conditionFormChecker, putField } from "../../../utils/form";
import {
    callDepartment,
    callJobPosition,
    callOfficerLv,
    callOfficerType,
    createFieldRegister1,
    createFieldRegisterOfficerInfo,
    createFieldRegisterUserInfo
} from "../../account/register/register.section";
import { callDocumentTypePerson, callUserRequestStatus, callUserRequestType } from "../company/mgt-tab/tab-4/tab4.section";

const AttachmentInlineList = defineAsyncComponent(() => import("../../../components/attachment/AttachmentInlineList"));

export const createColumns = () => {
    const columns = [
        createColumnTreeSelect({
            title: () => i18n.global.t("views.register.refDepartment"),
            dataIndex: "refUserTemp.refDepartment.nameTh",
            inputProp: () => ({
                list: callDepartment,
                renderLabel: ({ item }) => (
                    <div>
                        {item.fullCode} : {item.nameTh}
                    </div>
                ),
                filterWhere: ({ value }) => [
                    { name: "fullCode", operator: "like", value },
                    { logic: "OR", name: "nameTh", operator: "like", value }
                ],
                treeDefaultExpandAll: true
            }),
            search: { name: "refUserTemp.refDepartment.id" },
            width: 200
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.user.refJobPosition"),
            dataIndex: "refUserTemp.refJobPosition.nameTh",
            inputProp: () => ({ list: callJobPosition, optLabel: "nameTh" }),
            search: { name: "refUserTemp.refJobPosition.id" }
        }),
        createColumnInput({
            title: () => i18n.global.t("model.user.fullNameTh"),
            dataIndex: "refUserTemp.fullNameTh"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.user.refDocumentTypePerson"),
            dataIndex: "refUserTemp.refDocumentTypePerson.value",
            inputProp: () => ({ list: callDocumentTypePerson, optLabel: "value" }),
            search: { name: "refUserTemp.refDocumentTypePerson.id" }
        }),
        createColumnInput({
            title: () => i18n.global.t("model.user.idCard"),
            dataIndex: "refUserTemp.idCard"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.user.refOfficerType"),
            dataIndex: "refUserTemp.refOfficerType.value",
            inputProp: () => ({ list: callOfficerType, optLabel: "value" }),
            search: { name: "refUserTemp.refOfficerType.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.user.refOfficerLv"),
            dataIndex: "refUserTemp.refOfficerLv.value",
            inputProp: () => ({ list: callOfficerLv, optLabel: "value" }),
            search: { name: "refUserTemp.refOfficerLv.id" }
        }),
        createColumnDate({
            title: () => i18n.global.t("views.company.mgt.tab.approveDate"),
            dataIndex: "lastStatus.approve.0.statusDatetime",
            align: "center",
            width: 130
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refRequestType"),
            dataIndex: "refRequestType.value",
            inputProp: () => ({ list: callUserRequestType, optLabel: "value" }),
            search: { name: "refRequestType.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callUserRequestStatus, optLabel: "value" }),
            search: { name: "refStatus.id" }
        }),
        createColumnDatetime({
            title: () => i18n.global.t("model.createDate"),
            dataIndex: "createDate",
            align: "center"
        })
    ];
    return columns;
};

export const createField = ({ props, data, key = "", isLoading }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forOfficeRemark.0.remark`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.descToUser"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forOfficeRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forOfficeRemark[0].remark = value),
                            disabled: viewOnly,
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            ...createFieldRegister1({
                props,
                data,
                key: `${key}refUserTemp`,
                isLoading,
                onLinkIdCard: onLinkIdCard({ props, data, key, isLoading })
            }),
            ...createFieldRegisterOfficerInfo({ props, data, key: `${key}refUserTemp`, isLoading }),
            ...createFieldRegisterUserInfo({ props, data, key: `${key}refUserTemp`, isLoading }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}refUserTemp.attachments`,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "AttachmentInlineList" } })
                    );
                    return <AttachmentInlineList {...componentProp}></AttachmentInlineList>;
                }
            }
        ]
    });
};

export const onLinkIdCard = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    return d => {
        const result = [];
        if ((model.refUserTemp.idCard || "") !== (d?.idCard || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("views.register.idCard")
                })
            );
        if ((model.refUserTemp.birthDate || "") !== (d?.birthDate || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.birthDate")
                })
            );
        if ((model.refUserTemp.refNationality?.value || "") !== (d?.nationalityDesc || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.refNationality")
                })
            );
        if ((model.refUserTemp.refTitle?.value || "") !== (d?.titleName || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.refTitle")
                })
            );
        if ((model.refUserTemp.firstNameTh || "") !== (d?.firstNameTh || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.firstNameTh")
                })
            );
        if ((model.refUserTemp.middleNameTh || "") !== (d?.middleNameTh || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.middleNameTh")
                })
            );
        if ((model.refUserTemp.lastNameTh || "") !== (d?.lastNameTh || ""))
            result.push(
                i18n.global.t("views.register.linkWrong", {
                    text: i18n.global.t("model.user.lastNameTh")
                })
            );
        model.lastStatus.forOfficeRemark[0].remark = result.join("\r\n");
    };
};
